import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Calendar, Clock, ChevronRight, ArrowRight, BookOpen, ChevronDown, X } from 'lucide-react';
import { getCategoryConfig, CategoryBadge } from '../components/blog/BlogGraphics';
import VideoSection from '../components/VideoSection';
import SEOHead from '@/components/SEOHead';

const BlogPage = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts`);
      const data = await response.json();
      setBlogPosts(data.posts || []);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/categories`);
      const data = await response.json();
      setCategories(data.categories || ['All']);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const filteredPosts = activeCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeCategory);

  const featuredPost = blogPosts.find(post => post.featured);

  return (
    <div className="min-h-screen pt-20" data-testid="blog-page">
      <SEOHead 
        title="Legal Blog & Resources | Personal Injury Articles | Amy C. Brown Law"
        description="Learn about personal injury law, car accidents, maritime injuries, and your legal rights. Expert articles from Washington State personal injury attorney Amy C. Brown."
        pageType="blog"
      />
      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full text-primary mb-6">
              <BookOpen className="w-4 h-4" />
              <span className="text-sm font-medium">Legal Resources & Insights</span>
            </div>
            
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6">
              Learn About Your Legal Rights
            </h1>
            
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Expert insights on personal injury law, maritime claims, and protecting your rights. 
              Get the information you need to make informed decisions about your case.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full font-semibold hover:bg-primary/90 transition-all"
                data-testid="blog-hero-call"
              >
                <Phone className="w-5 h-5" />
                Free Consultation
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <VideoSection 
        title="Videos & Articles to Help You"
        subtitle="Watch videos and read articles below to understand your legal rights, learn what to expect, and see how I can help with your injury case."
        badge="Learning Center"
      />

      {/* Featured Post - Enhanced Design */}
      {featuredPost && (
        <section className="py-12 bg-white relative">
          <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-secondary/5 to-transparent" />
          
          <div className="container mx-auto px-4 md:px-6 max-w-7xl relative">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-300 to-transparent" />
              <span className="text-sm font-medium text-slate-500 uppercase tracking-wider">Featured Article</span>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-300 to-transparent" />
            </div>
            
            <Link 
              to={`/blog/${featuredPost.slug}`}
              className="group grid md:grid-cols-2 gap-0 bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 border border-slate-100"
              data-testid="featured-post"
            >
              <div className="relative h-72 md:h-auto overflow-hidden">
                {/* Category Gradient Overlay */}
                {(() => {
                  const config = getCategoryConfig(featuredPost.category);
                  return (
                    <div className={`absolute inset-0 bg-gradient-to-br ${config.gradient} opacity-0 group-hover:opacity-20 transition-opacity duration-500`} />
                  );
                })()}
                <img 
                  src={featuredPost.image}
                  alt={featuredPost.imageAlt || featuredPost.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <CategoryBadge category={featuredPost.category} size="md" />
                </div>
              </div>
              
              <div className="p-8 md:p-10 flex flex-col justify-center bg-gradient-to-br from-white to-slate-50">
                <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    {featuredPost.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    {featuredPost.readTime}
                  </span>
                </div>
                
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-4 group-hover:text-primary transition-colors">
                  {featuredPost.title}
                </h2>
                
                <p className="text-slate-600 mb-6 text-lg leading-relaxed">
                  {featuredPost.excerpt}
                </p>
                
                <span className="inline-flex items-center gap-2 text-primary font-semibold group-hover:gap-3 transition-all">
                  Read Full Article 
                  <ArrowRight className="w-5 h-5" />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Category Filter - Mobile Dropdown, Desktop Pills */}
      <section className="py-4 md:py-8 bg-slate-50 border-y border-slate-200 sticky top-16 z-30 backdrop-blur-sm bg-slate-50/95">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          
          {/* Mobile: Dropdown */}
          <div className="md:hidden relative">
            <button
              onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
              className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-slate-200 shadow-sm"
            >
              <span className="font-medium text-slate-700">
                Article Topics
              </span>
              <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${mobileFilterOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Dropdown Menu */}
            {mobileFilterOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg border border-slate-200 shadow-xl z-40 max-h-80 overflow-y-auto">
                {categories.map((category) => {
                  const config = getCategoryConfig(category);
                  const Icon = config.icon;
                  const isActive = activeCategory === category;
                  
                  return (
                    <button
                      key={category}
                      onClick={() => {
                        setActiveCategory(category);
                        setMobileFilterOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                        isActive 
                          ? 'bg-primary/10 text-primary font-medium' 
                          : 'hover:bg-slate-50 text-slate-700'
                      }`}
                    >
                      {category !== 'All' && <Icon className="w-4 h-4" />}
                      {category === 'All' ? 'All Categories' : category}
                      {isActive && <span className="ml-auto text-primary">✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Desktop: Pills */}
          <div className="hidden md:flex flex-wrap gap-3 justify-center">
            {categories.map((category) => {
              const config = getCategoryConfig(category);
              const Icon = config.icon;
              const isActive = activeCategory === category;
              
              return (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-all ${
                    isActive
                      ? `bg-gradient-to-r ${config.gradient} text-white shadow-lg`
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`category-${category.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {category !== 'All' && <Icon className="w-4 h-4" />}
                  {category}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid - Visual Cards */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1,2,3].map(i => (
                <div key={i} className="bg-slate-100 rounded-2xl h-96 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.filter(p => !p.featured).map((post) => {
                const config = getCategoryConfig(post.category);
                const Icon = config.icon;
                
                return (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100"
                    data-testid={`blog-card-${post.id}`}
                  >
                    {/* Image with Category Gradient */}
                    <div className="relative h-52 overflow-hidden">
                      <img 
                        src={post.image}
                        alt={post.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      
                      {/* Gradient Overlay */}
                      <div className={`absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent`} />
                      <div className={`absolute inset-0 bg-gradient-to-br ${config.gradient} opacity-0 group-hover:opacity-30 transition-opacity duration-500`} />
                      
                      {/* Category Icon Circle */}
                      <div className={`absolute top-4 right-4 p-3 bg-white/90 backdrop-blur-sm rounded-full shadow-lg group-hover:scale-110 transition-transform`}>
                        <Icon className={`w-5 h-5 ${config.textColor}`} />
                      </div>
                      
                      {/* Category Badge */}
                      <div className="absolute bottom-4 left-4">
                        <CategoryBadge category={post.category} size="sm" />
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-slate-400 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {post.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {post.readTime}
                        </span>
                      </div>
                      
                      <h3 className="font-heading text-lg font-bold text-secondary mb-3 group-hover:text-primary transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      
                      <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      
                      <span className="inline-flex items-center gap-1 text-primary font-semibold text-sm group-hover:gap-2 transition-all">
                        Read Article <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                    
                    {/* Bottom Accent Line */}
                    <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${config.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`} />
                  </Link>
                );
              })}
            </div>
          )}
          
          {filteredPosts.length === 0 && !loading && (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-600 mb-2">No articles found</h3>
              <p className="text-slate-500">Try selecting a different category</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section - Enhanced */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-coral-500 to-coral-600" />
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
        }} />
        
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Have Questions About Your Case?
          </h2>
          <p className="text-xl text-coral-100 mb-10 max-w-2xl mx-auto">
            Get a free, no-obligation consultation. I personally respond to every inquiry and will help you understand your options.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="group inline-flex items-center justify-center gap-2 bg-white text-secondary px-8 py-4 rounded-full font-bold hover:bg-coral-50 transition-all shadow-xl hover:shadow-2xl"
              data-testid="blog-cta-call"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
